import type { DagSlice } from "./api";

export const DEFAULT_DAG_VIEW = {
  radius: 4, hoverRadius: 6, selectedRadius: 7, ringWidth: 1.5,
  padding: 40, maxAutoZoom: 2, cooldownTicks: 100, pointerPadding: 1, minPointerRadius: 12,
  labelSize: 10, labelOffset: 8,
};

export interface VizNode {
  id: string;
  kind: number;
  status: string;
  witness_round: number | null;
  amount: string;
  x?: number;
  y?: number;
}
export interface VizLink { source: string; target: string }
export interface VizData { nodes: VizNode[]; links: VizLink[]; signature: string }

// Equivalent observations retain simulation objects and the member's camera.
export function graphData(slice: DagSlice, previous?: VizData): VizData {
  const nodes = slice.nodes.map(n => ({ id: n.tx_id, kind: n.kind, status: n.status, witness_round: n.witness_round, amount: n.amount }));
  const ids = new Set(nodes.map(n => n.id));
  const links = slice.edges.filter(e => ids.has(e.child_tx_id) && ids.has(e.parent_tx_id))
    .map(e => ({ source: e.child_tx_id, target: e.parent_tx_id }));
  const signature = JSON.stringify({ nodes, links });
  if (previous?.signature === signature) return previous;
  const old = new Map(previous?.nodes.map(n => [n.id, n]));
  return { nodes: nodes.map(n => ({ ...old.get(n.id), ...n })), links, signature };
}

export function nodeRadius(selected: boolean, hovered: boolean) {
  return selected ? DEFAULT_DAG_VIEW.selectedRadius : hovered ? DEFAULT_DAG_VIEW.hoverRadius : DEFAULT_DAG_VIEW.radius;
}

export function pointerRadius(selected: boolean, hovered: boolean, scale: number) {
  return Math.max(nodeRadius(selected, hovered) + DEFAULT_DAG_VIEW.ringWidth / 2 + DEFAULT_DAG_VIEW.pointerPadding / scale, DEFAULT_DAG_VIEW.minPointerRadius / scale);
}

export function fitGraph(bounds: { x: [number, number]; y: [number, number] } | null, width: number, height: number, policy = DEFAULT_DAG_VIEW) {
  if (!bounds || ![...bounds.x, ...bounds.y, width, height].every(Number.isFinite)) return null;
  const w = width - policy.padding * 2;
  const h = height - policy.padding * 2;
  const dx = bounds.x[1] - bounds.x[0];
  const dy = bounds.y[1] - bounds.y[0];
  if (w <= 0 || h <= 0 || dx <= 0 || dy <= 0) return null;
  return {
    x: (bounds.x[0] + bounds.x[1]) / 2,
    y: (bounds.y[0] + bounds.y[1]) / 2,
    zoom: Math.min(policy.maxAutoZoom, w / dx, h / dy),
  };
}
